import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { DailogBox } from "../../../../../components/Elements/DailogBox/DailogBox";
import {
  fetchLinkedItems,
  fetchAllLocations,
  createMapping,
  clearMappingStatus,
} from "../../../../../redux/customer/customerSlice";
import PopupCard from "../../../../../components/Cards/PopupCard/PopupCard";
import { createPortal } from "react-dom";
import { useParams } from "react-router-dom";
const LinkLocationPopup = ({
  onOpen,
  onClose,
  departmentName,
  departmentId,
}) => {
  const dispatch = useDispatch();
  const { linkedDepartments, locationList, loadingParams, mappingSuccess, error, currentCustomer } =
    useSelector((state) => state.customer);
  const {userId} =useParams();
  const [selectedLocations, setSelectedLocations] = useState([]);

  useEffect(() => {
    if (onOpen && departmentId && userId) {
      dispatch(fetchLinkedItems({ id: departmentId, userId }));
      dispatch(fetchAllLocations(userId));
    }
  }, [onOpen, departmentId, userId, dispatch]);

  const [popup, setPopup] = useState({
    type: "",
    visible: false,
    title: "",
    message: "",
  });

  // Auto-close popup
  useEffect(() => {
    if (popup.visible) {
      const timer = setTimeout(() => {
        setPopup((prev) => ({ ...prev, visible: false }));
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [popup.visible]);

  useEffect(() => {
    if (linkedDepartments && Array.isArray(linkedDepartments)) {
      setSelectedLocations(
        linkedDepartments.filter((item) => item && item._id).map((item) => item._id),
      );
    } else {
      setSelectedLocations([]);
    }
  }, [linkedDepartments]);

  // Use the global location list from Redux
  const allLocations = (locationList || []).filter((loc) => loc && loc._id);

  const handleCheckboxChange = (locationId) => {
    setSelectedLocations((prev) =>
      prev.includes(locationId)
        ? prev.filter((id) => id !== locationId)
        : [...prev, locationId],
    );
  };

  const handleSave = async () => {
    try {
      const payload = {
        userId,
        data: {
          parentId: departmentId,
          childIds: selectedLocations,
        },
      };
      const res = await dispatch(createMapping(payload)).unwrap();
      
      if (res?.success) {
        setPopup({
          type: "success",
          visible: true,
          title: "Success",
          message: res?.message || "Locations mapped successfully",
        });

        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        setPopup({
          type: "error",
          visible: true,
          title: "Error",
          message: res?.message || "Failed to map locations",
        });
      }
    } catch (err) {
      console.error("Mapping error:", err);
      const errorMessage = 
        err?.message || 
        err?.data?.message || 
        (typeof err === 'string' ? err : "Failed to map locations");

      setPopup({
        type: "error",
        visible: true,
        title: "Error",
        message: errorMessage,
      });
    }
  };

  return (
    <DailogBox open={onOpen} onCancel={onClose} width="90%" maxWidth="750px">
      <div className="p-8 relative">
        {/* Title */}
        <h2 className="text-[22px] font-bold text-[#06513D] mb-8">
          Add Location
        </h2>

        {/* Checkbox Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-7 gap-x-4 mb-12">
          {[...allLocations]
            .sort((a, b) => {
              // Get the index in the already linked items list
              const aIndex = (linkedDepartments || []).findIndex(
                (item) => item?._id === a._id,
              );
              const bIndex = (linkedDepartments || []).some(
                (item) => item?._id === b._id,
              );

              if (
                aIndex !== -1 &&
                (linkedDepartments || []).some((item) => item?._id === b._id)
              ) {
                const bIndexActual = (linkedDepartments || []).findIndex(
                  (item) => item?._id === b._id,
                );
                return aIndex - bIndexActual;
              }

              const aIsLinked = aIndex !== -1;
              const bIsLinked = (linkedDepartments || []).some(
                (item) => item?._id === b._id,
              );
              return bIsLinked - aIsLinked;
            })
            .map((loc) => (
              <div key={loc._id} className="flex items-center gap-4">
                <input
                  type="checkbox"
                  id={loc._id}
                  checked={selectedLocations.includes(loc._id)}
                  onChange={() => handleCheckboxChange(loc._id)}
                  className="w-5 h-5 rounded-md border-[#3BA2B959] cursor-pointer accent-[#06513D]"
                />
                <label
                  htmlFor={loc._id}
                  className="text-[17px] text-[#2D2D2D] font-medium cursor-pointer select-none"
                >
                  {loc.name || loc.location_name}
                </label>
              </div>
            ))}
          {allLocations.length === 0 &&
            !loadingParams.fetchLinked &&
            !loadingParams.fetchLocation && (
              <p className="text-gray-400 italic">No locations available</p>
            )}
          {(loadingParams.fetchLinked || loadingParams.fetchLocation) && (
            <p className="text-[#06513D] animate-pulse">Loading locations...</p>
          )}
        </div>

        {/* Success/Error Popups */}
        {popup.visible &&
          createPortal(
            <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black bg-opacity-40 px-4">
              <PopupCard
                type={popup.type}
                title={popup.title}
                message={popup.message}
              />
            </div>,
            document.body,
          )}

        {/* Footer Buttons */}
        <div className="flex justify-end gap-5">
          <button
            onClick={onClose}
            className="px-8 py-2.5 border border-[#E5EAEF] text-[#2D2D2D] font-medium rounded-xl text-[16px] hover:bg-gray-50 transition-colors shadow-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loadingParams?.createMapping}
            className={`px-10 py-2.5 bg-[#06513D] text-white rounded-xl text-[16px] font-bold transition-all shadow-sm ${
              loadingParams?.createMapping
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-[#054131] hover:shadow-md"
            }`}
          >
            {loadingParams?.createMapping ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </DailogBox>
  );
};

export default LinkLocationPopup;
